import { useState, useEffect } from "react";
import { DownloadSimple, X } from "@phosphor-icons/react";

/**
 * Sticky "Install Korporate" banner for public pages.
 * - Captures the native beforeinstallprompt event (Chrome / Edge on Android & desktop)
 * - Only renders when the browser is actually installable AND the user hasn't dismissed it in the last 7 days
 * - Hides itself when the app is already installed (standalone display-mode)
 * - Zero flashing on non-supporting browsers (iOS Safari, Firefox) — just stays hidden
 */
const DISMISS_KEY = "korp-install-dismissed-at";
const DISMISS_WINDOW_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

const isStandalone = () =>
  (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
  (typeof navigator !== "undefined" && navigator.standalone);

const isRecentlyDismissed = () => {
  try {
    const at = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    return at && Date.now() - at < DISMISS_WINDOW_MS;
  } catch { return false; }
};

const InstallPrompt = () => {
  const [prompt, setPrompt] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isStandalone() || isRecentlyDismissed()) return;
    const onBefore = (e) => {
      e.preventDefault(); // Stop Chrome's default mini-infobar
      setPrompt(e);
      setVisible(true);
    };
    const onInstalled = () => {
      setVisible(false);
      setPrompt(null);
      try { localStorage.removeItem(DISMISS_KEY); } catch { /* noop */ }
    };
    window.addEventListener("beforeinstallprompt", onBefore);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBefore);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!prompt) return;
    try {
      await prompt.prompt();
      await prompt.userChoice; // We don't care about the outcome — browser handles it
    } catch { /* user dismissed via browser UI */ }
    setPrompt(null);
    setVisible(false);
  };

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* noop */ }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 bg-[#0A0D14] border-t-2 border-[#FACC15] shadow-2xl animate-in slide-in-from-bottom duration-300"
      data-testid="install-prompt-banner"
      role="dialog"
      aria-label="Install Korporate app"
    >
      <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
        <div className="w-10 h-10 bg-[#FACC15] text-[#0A0D14] flex items-center justify-center shrink-0 font-display text-xl">K</div>
        <div className="flex-1 min-w-0">
          <div className="font-mono-k text-xs tracking-widest-k text-[#FACC15]">— INSTALL KORPORATE</div>
          <div className="font-mono-k text-xs text-[#DDDDDD] truncate">Add to home screen for faster access + push alerts on new leads.</div>
        </div>
        <button
          onClick={install}
          className="k-btn primary text-xs px-3 py-2 whitespace-nowrap"
          data-testid="install-prompt-accept"
        >
          <DownloadSimple size={14} /> INSTALL
        </button>
        <button
          onClick={dismiss}
          className="text-[#A1A1AA] hover:text-white p-1 shrink-0"
          aria-label="Dismiss install prompt"
          data-testid="install-prompt-dismiss"
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
};

export default InstallPrompt;
